import { Tooltip } from "antd"
import type { TileState } from "@csbi/contracts"

/**
 * One cell of the matrix.
 *
 * **The hover note is the only explanation these four symbols get** — the legend strip was removed, so the
 * popup cannot be a nicety: it has to be fast and legible. Hence antd `Tooltip` rather than the native `title`:
 * a native title waits ~1s and is styled by the operating system, far too weak to be the only entry point.
 *
 * (Performance first led me to the native title, on the grounds that "60 deployments = 5400 cells = 5400
 * portals". That reasoning was wrong: rc-tooltip creates its popup **on first hover**, so an un-hovered
 * Tooltip is an empty shell — and with pagination a screen holds at most 15 × N cells anyway.)
 *
 * **Shape and character are distinguishable on their own, without colour.** For colour-blind readers, in print
 * and under forced-colors, `✓ / ○ / – / !` remain four different characters; colour only speeds recognition.
 */

type CellStyle = { glyph: string; bg: string; fg: string; border: string; label: string; hint: string }

export const CELL_STYLES: Record<TileState, CellStyle> = {
  ok: {
    glyph: "✓",
    bg: "var(--csbi-status-good)",
    fg: "#ffffff",
    border: "transparent",
    label: "Linked",
    hint: "Collected rows exist for this metric on this deployment in the last 90 days.",
  },
  empty: {
    // Hollow = there is nothing here. A solid grey reads as "there is something here, it is just grey"
    glyph: "○",
    bg: "transparent",
    fg: "var(--csbi-ink-muted)",
    border: "var(--csbi-gridline)",
    label: "No data",
    hint: "The pipeline works — this deployment produced no rows of this kind. That is a fact about the data, not a defect.",
  },
  unsupported: {
    glyph: "–",
    bg: "var(--csbi-gridline)",
    fg: "var(--csbi-ink-muted)",
    border: "transparent",
    label: "Unsupported",
    hint: "This deployment's srv version does not expose this metric yet. It arrives with a normal srv release.",
  },
  stale: {
    // Dark text is required on the amber: white on brand amber is 1.6:1, this near-brown is 8.6:1
    glyph: "!",
    bg: "var(--csbi-status-warning)",
    fg: "#3a2c00",
    border: "transparent",
    label: "Collection failing",
    hint: "Collection has failed repeatedly for this module. What the tiles show is the last successful collection.",
  },
}

export const CELL_SIZE = 20

export function CoverageCell({ state, tenantName }: { state: TileState; tenantName: string }) {
  const s = CELL_STYLES[state]
  return (
    <Tooltip
      // A cell is hovered for a glance; 0.15s filters out the incidental triggers of moving across the table without making anyone wait
      mouseEnterDelay={0.15}
      title={
        <span style={{ display: "block", maxWidth: 260 }}>
          <strong>{s.label}</strong> · {tenantName}
          <br />
          {s.hint}
        </span>
      }
    >
    <span
      // The table cell already has an aria column header; repeating the state here keeps a screen reader from announcing only a ✓
      aria-label={`${tenantName}: ${s.label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: CELL_SIZE,
        height: CELL_SIZE,
        borderRadius: 4,
        background: s.bg,
        color: s.fg,
        border: `1px ${state === "empty" ? "dashed" : "solid"} ${s.border}`,
        fontSize: 11,
        lineHeight: 1,
        fontWeight: 700,
      }}
    >
      {s.glyph}
    </span>
    </Tooltip>
  )
}
